import React from 'react';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import { withTranslation, WithTranslation } from 'react-i18next';
import TablePagination from '@mui/material/TablePagination';
import { LabelDisplayedRowsArgs } from '@mui/material/TablePagination/TablePagination';
import './table-styles.scss';
import { TableHeader } from './TableHeader';
import { TableRow } from './TableRow';
import { Loader } from '../loader/Loader';
import { tablePaginationDefaultValues } from '../../utils/constants/common';

export interface IPaginationData {
	page: number;
	rowsPerPage: number;
}

export interface ITableProps extends WithTranslation {
	minWidth?: string;
	wrapperClass?: string;
	maxWidth?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	columns: any;
	first: boolean;
	last: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	style?: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	data: any;
	rowsPerPage?: number;
	rowsPerPageOptions?: number[];
	page?: number;
	count: number;
	showFooter?: boolean;
	onRowsPerPageChange?: (d: IPaginationData) => void;
	onPageChange?: (d: IPaginationData) => void;
	withPagination?: true;
	noDataMessage?: string;
	loading?: boolean;
}

class TableTemp extends React.Component<ITableProps, IPaginationData> {
	state = {
		page: tablePaginationDefaultValues.page as number,
		rowsPerPage: tablePaginationDefaultValues.size as number,
	};

	componentDidMount(): void {
		if (this.props.withPagination) {
			this.setState(({ page, rowsPerPage }) => ({
				page: this.props.page || page,
				rowsPerPage: this.props.rowsPerPage || rowsPerPage,
			}));
		}
	}

	onPageChange = (page: number) => {
		this.setState({ page });
	};

	onRowsPerPageChange = (rowsPerPage: number) => {
		this.setState({ rowsPerPage });
	};

	public handleChangePage = (
		event: React.MouseEvent<HTMLButtonElement> | null,
		page: number,
	) => {
		this.onPageChange(page);
		if (this.props.onPageChange) {
			this.props.onPageChange({
				page,
				rowsPerPage: this.state.rowsPerPage,
			});
		}
	};

	public handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const newValue = parseInt(event.target.value, 10);
		this.onRowsPerPageChange(newValue);

		const totalPages = Math.floor(this.props.count / newValue);
		let { page } = this.state;

		if (page > totalPages) {
			page = totalPages;
		}

		if (this.props.onRowsPerPageChange) {
			this.props.onRowsPerPageChange({
				page,
				rowsPerPage: newValue,
			});
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public renderRow = (row: any, idx: number) => (
		<TableRow row={row} columns={this.props.columns} key={idx} />
	);

	public render() {
		const {
			columns,
			data,
			minWidth,
			maxWidth,
			showFooter = true,
			count,
			wrapperClass = '',
			style,
			rowsPerPageOptions = [8, 15, 20],
			withPagination,
			t,
			noDataMessage,
			loading,
		} = this.props;

		const { page, rowsPerPage } = this.state;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let paginatedData: any;

		if (!withPagination && data) {
			paginatedData = data.slice(
				page * rowsPerPage,
				page * rowsPerPage + rowsPerPage,
			);
		}

		const labelDisplayedRows = ({
			from,
			to,
			count: totalCount,
		}: LabelDisplayedRowsArgs) => {
			const startCount =
				from > totalCount
					? (totalCount - rowsPerPage > 0 && totalCount - rowsPerPage) || 1
					: from;

			const endCount = to === -1 ? totalCount : to;

			const total = totalCount !== -1 ? totalCount : to;

			return `${startCount}-${endCount} ${t('of')} ${total}`;
		};

		return (
			<>
				<div className={`ui-table ${wrapperClass}`} style={style}>
					<MuiTable style={{ minWidth, maxWidth }}>
						<TableHeader columns={columns} />
						<Loader isLoading={loading}>
							{!!count && (
								<TableBody>
									{withPagination
										? data.map(this.renderRow)
										: paginatedData.map(this.renderRow)}
								</TableBody>
							)}
						</Loader>
					</MuiTable>
				</div>
				{!count && <p className="table-fallback-message">{noDataMessage}</p>}
				{showFooter && (
					<div className="footer-pagination">
						<TablePagination
							rowsPerPageOptions={rowsPerPageOptions}
							component="div"
							count={count}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={this.handleChangePage}
							onRowsPerPageChange={this.handleChangeRowsPerPage}
							labelRowsPerPage={`${t('rows_per_page')}:`}
							labelDisplayedRows={labelDisplayedRows}
						/>
					</div>
				)}
			</>
		);
	}
}

export const Table = withTranslation('common')(TableTemp);
